import {
  Menu, HelpCircle, BookOpen, Sparkles, Undo2, Redo2, Download, Upload,
  Image, Cloud, Share2, Copy, Play, ListTree, Lightbulb, Heart, LogIn, Trash2, Bug, Wand2,
  Rocket, FolderUp, FileJson, Bot, Cog,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from '@/i18n/LanguageContext';
import { useSystemSwitches } from '@/hooks/useSystemSwitches';
import { IS_WEB } from '@/lib/buildMode';
import { BRAND_NAVY } from '@/constants/brandColors';

export interface TopBarMobileMenuProps {
  isAuthenticated: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  onSave: () => void;
  onLoad: () => void;
  onRepoUpload?: () => void;
  onExportPNG: () => void;
  onSimulate: () => void;
  onClear: () => void;
  onImportJson: () => void;
  onCopyJson: () => void;
  onHelp: () => void;
  onAiJson: () => void;
  onAiAssistant: () => void;
  onPreview: () => void;
  onLogin?: () => void;
  onCloudWorkspace?: () => void;
  onShareWorkspace?: () => void;
  onFeatureRequest?: () => void;
  onBugReport?: () => void;
  onDonate?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onReopenWizard?: () => void;
}

export function TopBarMobileMenu(props: TopBarMobileMenuProps) {
  const { t } = useTranslation();
  const {
    isAuthenticated, canUndo, canRedo,
    onSave, onLoad, onRepoUpload, onExportPNG, onSimulate, onClear,
    onImportJson, onCopyJson, onHelp, onAiJson, onAiAssistant, onPreview,
    onLogin, onCloudWorkspace, onShareWorkspace, onFeatureRequest, onBugReport, onDonate,
    onUndo, onRedo, onReopenWizard,
  } = props;
  const { switches } = useSystemSwitches();
  const processEngineDisabled = !switches.processEngine;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-black/10"
          style={{ color: BRAND_NAVY }}
          aria-label="Open menu"
          data-testid="topbar-menu"
        >
          <Menu className="w-5 h-5" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {onReopenWizard && (
          <>
            <DropdownMenuItem onClick={onReopenWizard} data-testid="topbar-menu-wizard-mobile">
              <Wand2 className="w-4 h-4 mr-2" /> Welcome
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={onHelp}>
          <HelpCircle className="w-4 h-4 mr-2" /> {t('help')}
        </DropdownMenuItem>
        {isAuthenticated && (
          <>
            <DropdownMenuItem onClick={onAiJson}>
              <FileJson className="w-4 h-4 mr-2" /> AI & JSON
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onAiAssistant}>
              <Bot className="w-4 h-4 mr-2" /> AI Assistant
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onUndo} disabled={!canUndo}>
          <Undo2 className="w-4 h-4 mr-2" /> {t('undo') || 'Ångra'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onRedo} disabled={!canRedo}>
          <Redo2 className="w-4 h-4 mr-2" /> {t('redo') || 'Gör om'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSave}>
          <Download className="w-4 h-4 mr-2" /> {t('saveFile')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onLoad}>
          <Upload className="w-4 h-4 mr-2" /> {t('loadFile')}
        </DropdownMenuItem>
        {onRepoUpload && (
          <DropdownMenuItem onClick={onRepoUpload}>
            <FolderUp className="w-4 h-4 mr-2" /> Ladda upp repository (.zip)
          </DropdownMenuItem>
        )}
        {isAuthenticated && (
          <>
            <DropdownMenuItem onClick={onExportPNG}>
              <Image className="w-4 h-4 mr-2" /> {t('exportPng')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onCloudWorkspace}>
              <Cloud className="w-4 h-4 mr-2" /> Molnarbetsytor
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onShareWorkspace}>
              <Share2 className="w-4 h-4 mr-2" /> Dela arbetsyta
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onImportJson}>
          <Upload className="w-4 h-4 mr-2" /> {t('import_')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onCopyJson}>
          <Copy className="w-4 h-4 mr-2" /> {t('copyJson')}
        </DropdownMenuItem>
        {isAuthenticated && (
          <>
            <DropdownMenuItem disabled title="Tillfälligt avstängd / Temporarily disabled">
              <Play className="w-4 h-4 mr-2" /> {t('simulate')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onPreview}>
              <ListTree className="w-4 h-4 mr-2" /> Overview
            </DropdownMenuItem>
          </>
        )}
        {IS_WEB && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onBugReport}>
              <Bug className="w-4 h-4 mr-2" /> Report a Bug
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onFeatureRequest}>
              <Lightbulb className="w-4 h-4 mr-2" /> Feature Request
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDonate}>
              <Heart className="w-4 h-4 mr-2" /> {t('donate')}
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogin}>
          <LogIn className="w-4 h-4 mr-2" /> Logga in
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onClear} className="text-destructive">
          <Trash2 className="w-4 h-4 mr-2" /> {t('clear')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
